import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Lesson, LessonCreateRequest } from '@court-sync/shared';
import { lessonService } from '../services/lesson';
import { STALE_TIME, queryClient } from '../queryClient';
import { queryKeys, type LessonListFilters } from './keys';
import { unwrapApiData, unwrapApiDataOrEmpty } from './unwrap';
import { useLessonCalendarStore } from '../store/lessonCalendar';
import { EMPTY_LESSON_FILTERS, serializeFilters } from './stableFilters';

function indexById(items: Lesson[]): Record<string, Lesson> {
  return items.reduce<Record<string, Lesson>>((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
}

function refreshLessonCalendarForAcademy(academyId: string | undefined) {
  if (!academyId) return;
  void useLessonCalendarStore.getState().refreshAfterLessonMutation(academyId);
}

/** Invalidate all lesson list caches (optionally scoped to one academy). */
export function invalidateLessonsQueries(academyId?: string): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: queryKeys.lessons.lists(),
    ...(academyId ? { predicate: (q) => q.queryKey.includes(academyId) } : {}),
  });
}

export async function fetchLessonsQuery(
  filters?: LessonListFilters,
  options?: { force?: boolean; merge?: boolean }
): Promise<void> {
  const academyId = filters?.academyId ?? '';
  const key = queryKeys.lessons.list(academyId, filters ?? {});

  const load = async (): Promise<Lesson[]> => {
    const response = await lessonService.getLessons(filters);
    return unwrapApiDataOrEmpty(response);
  };

  const lessons = options?.force
    ? await load()
    : await queryClient.fetchQuery({ queryKey: key, queryFn: load, staleTime: STALE_TIME.MEDIUM });

  if (options?.merge) {
    queryClient.setQueryData<Lesson[]>(key, (old) => {
      const merged = { ...indexById(old ?? []), ...indexById(lessons) };
      return Object.values(merged);
    });
  } else {
    queryClient.setQueryData(key, lessons);
  }
}

export function useLessons(academyId?: string, listFilters: LessonListFilters = EMPTY_LESSON_FILTERS) {
  const queryClient = useQueryClient();
  const serializedListFilters = serializeFilters(listFilters);
  const filters = useMemo(
    () => ({ ...listFilters, academyId }),
    [academyId, serializedListFilters, listFilters]
  );
  const queryKey = queryKeys.lessons.list(academyId ?? '', filters);

  const { data: lessons = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await lessonService.getLessons(filters);
      return unwrapApiDataOrEmpty(response);
    },
    enabled: !!academyId,
    staleTime: STALE_TIME.MEDIUM,
  });

  const invalidateLists = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.lessons.lists() });
  }, [queryClient]);

  const createMutation = useMutation({
    mutationFn: async (data: LessonCreateRequest) => {
      const response = await lessonService.createLesson(data);
      const id = unwrapApiData(response, 'Failed to create lesson').id;
      const detail = await lessonService.getLesson(id);
      return unwrapApiData(detail, 'Failed to fetch created lesson');
    },
    onSuccess: (created) => {
      invalidateLists();
      refreshLessonCalendarForAcademy(created.academyId);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Omit<Lesson, 'id' | 'createdAt' | 'updatedAt' | 'currentEnrollment'>>;
    }) => {
      await lessonService.updateLesson(id, data);
      const detail = await lessonService.getLesson(id);
      return unwrapApiData(detail, 'Failed to fetch updated lesson');
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.lessons.lists() });
      const previous = queryClient.getQueriesData<Lesson[]>({ queryKey: queryKeys.lessons.lists() });
      queryClient.setQueriesData<Lesson[]>({ queryKey: queryKeys.lessons.lists() }, (old) =>
        old?.map((lesson) =>
          lesson.id === id ? { ...lesson, ...data, updatedAt: new Date() } : lesson
        )
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      context?.previous.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSuccess: (updated) => {
      invalidateLists();
      refreshLessonCalendarForAcademy(updated.academyId);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ id, academyId: lessonAcademyId }: { id: string; academyId?: string }) => {
      await lessonService.deleteLesson(id);
      return lessonAcademyId;
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.lessons.lists() });
      const previous = queryClient.getQueriesData<Lesson[]>({ queryKey: queryKeys.lessons.lists() });
      queryClient.setQueriesData<Lesson[]>({ queryKey: queryKeys.lessons.lists() }, (old) =>
        old?.filter((lesson) => lesson.id !== id)
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      context?.previous.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSuccess: (lessonAcademyId) => {
      invalidateLists();
      refreshLessonCalendarForAcademy(lessonAcademyId);
    },
  });

  const getLessonById = useCallback(
    (id: string) => lessons.find((l) => l.id === id),
    [lessons]
  );

  const refresh = useCallback(() => refetch(), [refetch]);

  const fetchLessons = useCallback(
    (f?: LessonListFilters, opts?: { force?: boolean; merge?: boolean }) =>
      fetchLessonsQuery(f ? { ...listFilters, academyId, ...f } : filters, opts),
    [academyId, filters, listFilters]
  );

  return {
    lessons,
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refresh,
    fetchLessons,
    createLesson: createMutation.mutateAsync,
    updateLesson: (id: string, data: Parameters<typeof updateMutation.mutateAsync>[0]['data']) =>
      updateMutation.mutateAsync({ id, data }),
    deleteLesson: (id: string) => {
      const lesson = getLessonById(id);
      return deleteMutation.mutateAsync({ id, academyId: lesson?.academyId });
    },
    getLessonById,
  };
}

/** Remove lessons for a deleted term from all list caches. */
export function removeLessonsByTermIdFromCache(termId: string): void {
  queryClient.setQueriesData<Lesson[]>({ queryKey: queryKeys.lessons.lists() }, (old) =>
    old?.filter((lesson) => lesson.termId !== termId)
  );
}
